/**
 * TIER CALCULATION - pure functions, no KV/env dependencies
 *
 * Extracted from data-pipeline.js so the math is unit-testable and the
 * penalty curve can be tuned without touching pipeline plumbing.
 *
 * The model:
 *   individualFunding = grassroots (<$200) + itemized (>$200)
 *   itemizedShare     = itemized / individualFunding   ("of the people who
 *                       gave, how reliant are you on big checks?")
 *   trust anchor      = allowed itemizedShare before penalties, derived from
 *                       donor-concentration data (Nakamoto coefficient)
 *   penalty           = capped quadratic on the excess over the anchor
 *   tier              = individualFunding % of totalRaised, minus penalty,
 *                       measured against thresholds shifted by PAC penalties
 */

// The July 2026 model: capped quadratic with a floor. Superseded by the
// excess-money model below (#42); kept for simulation and comparison.
export const CAPPED_QUADRATIC_OPTIONS = {
  penaltyModel: 'capped-quadratic',
  quadraticDivisor: 20, // penalty = excess^2 / divisor (original curve)
  penaltyCap: 40, // max points a concentration penalty can remove
  floorAtZero: true, // individualFundingPercent never goes negative
  // Concentration data is only trusted when the itemized collection actually
  // covered the member's reported itemized total. Early-cycle snapshots with
  // a handful of transactions otherwise trigger the harshest anchor.
  minConcentrationCoverage: 0.5, // collected $ must be >= 50% of FEC itemized $
  minUniqueDonors: 10,
};

// Step 4 corrected (issue #42). The capped quadratic measures the excess in
// points of INDIVIDUAL money, squares it, and subtracts it from a share of
// TOTAL money - two different denominators. That mismatch is why raw
// penalties reached 200-390 points, which is why a 40-point cap and a floor
// at zero were bolted on in July 2026, which is why 145 members ended up on
// an identical 0.
//
// 'excess-money' keeps the settled design - itemized money counts as
// individual support, up to an anchor set by how concentrated the donor base
// is - and applies it in money: itemized money ABOVE the anchor stops
// counting as people-funding. Same units throughout, so no divisor, cap or
// floor is needed; a score can never fall below the small-donor share.
export const EXCESS_MONEY_OPTIONS = {
  penaltyModel: 'excess-money',
  floorAtZero: false, // cannot go negative by construction
  minConcentrationCoverage: 0.5,
  minUniqueDonors: 10,
};

// What production uses.
export const DEFAULT_OPTIONS = EXCESS_MONEY_OPTIONS;

// Pre-July-2026: uncapped quadratic, no floor (produced negative scores).
export const LEGACY_OPTIONS = {
  penaltyModel: 'capped-quadratic',
  quadraticDivisor: 20,
  penaltyCap: Infinity,
  floorAtZero: false,
  minConcentrationCoverage: 0,
  minUniqueDonors: 0,
};

// FEC two-year transaction periods are named by the even END year:
// 2025 and 2026 both belong to cycle 2026.
export function cycleForYear(year) {
  return year % 2 === 0 ? year : year + 1;
}

/**
 * The cycle the jobs grade and publish on a given day (REBUILD_SPEC §9, the
 * New Year rollover): a member's grade is their most recent complete cycle
 * until the new one has its first quarterly report. On 1 January of an odd
 * year cycleForYear moves to a cycle with almost no money in it yet; grading
 * on it would empty every grade. So the finished cycle stays the graded one
 * until 1 May (the Q1 report is due 15 April; a fortnight for the FEC to
 * process it and the refresh to run), then the new cycle takes over.
 */
export function gradingCycle(date = new Date()) {
  const year = date.getUTCFullYear();
  if (year % 2 === 1 && date.getUTCMonth() < 4) {
    return year - 1;
  }
  return cycleForYear(year);
}

// How much more (or less) concerning each kind of PAC money is, for the PAC
// bar (calculateTransparencyPenalty). A simulation can pass its own
// (options.pacWeights).
//
// Version A (owner, 2026-10-09, #57): FEC designation B ("lobbyist or
// registrant PAC") no longer counts extra. It covers about 77% of PAC money
// to members, union and company PACs alike, so it told us nothing; how far
// a PAC's money comes from people is now measured directly (PAC_TRACING).
export const PAC_WEIGHTS = {
  super: 2.0, // committee type O: Super PACs are 2x more concerning
  candidate: 0.3, // committee type P: candidate committees 70% less concerning
  leadership: 1.5, // designation D: another politician's PAC (1.0-1.5 being simulated)
  lobbyist: 1.0, // designation B: lobbyist/registrant PAC (1.5 until 2026-10-09)
  authorized: 0.15, // designation P/A: candidate/authorized, 85% less concerning
};

// PAC money traced back to the people who funded each PAC (#57, owner
// 2026-10-09). `credit`: the share of a PAC's people-funded money that
// counts toward the member, because a PAC's leaders choose where its donors'
// money goes, not the donors (sensitivity simulated at 25% and 100%).
// `depth`: how far back money is followed: 1 is the PAC's own donors, 2 also
// the donors of the committees that gave to the PAC ("one level deeper").
export const PAC_TRACING = { credit: 0.5, depth: 2 };

export function getPACTransparencyWeight(committee_type, designation, w = PAC_WEIGHTS) {
  let weight = 1.0;

  if (committee_type === 'O') {
    weight *= w.super;
  } else if (committee_type === 'P') {
    weight *= w.candidate;
  }

  if (designation === 'D') {
    weight *= w.leadership;
  } else if (designation === 'B') {
    weight *= w.lobbyist;
  } else if (designation === 'P' || designation === 'A') {
    weight *= w.authorized;
  }

  return weight;
}

export function getCommitteeCategory(committee_type, designation) {
  if (committee_type === 'O') {
    return 'Super PAC';
  }
  if (designation === 'D') {
    return 'Leadership PAC';
  }
  if (designation === 'B') {
    return 'Lobbyist PAC';
  }
  if (committee_type === 'P' || designation === 'P' || designation === 'A') {
    return 'Candidate Committee';
  }
  if (committee_type === 'Q') {
    return 'Qualified PAC';
  }
  if (committee_type === 'N') {
    return 'Nonqualified PAC';
  }
  if (designation === 'U') {
    return 'Unauthorized PAC';
  }
  return 'Other PAC';
}

// Fallback tier from raw grassroots percentage (no PAC/concentration data)
export function calculateTier(grassrootsPercent, totalRaised) {
  if (totalRaised === 0) {
    return 'N/A';
  }

  if (grassrootsPercent >= 90) {
    return 'S';
  }
  if (grassrootsPercent >= 75) {
    return 'A';
  }
  if (grassrootsPercent >= 60) {
    return 'B';
  }
  if (grassrootsPercent >= 45) {
    return 'C';
  }
  if (grassrootsPercent >= 30) {
    return 'D';
  }
  if (grassrootsPercent >= 15) {
    return 'E';
  }
  return 'F';
}

// A concentration snapshot is only usable when it plausibly represents the
// member's full itemized donor base for the cycle.
export function isConcentrationReliable(member, concentration, options = DEFAULT_OPTIONS) {
  if (
    !concentration ||
    concentration.nakamotoCoefficient === undefined ||
    concentration.uniqueDonors === undefined
  ) {
    return false;
  }

  if (concentration.uniqueDonors < options.minUniqueDonors) {
    return false;
  }

  const reportedItemized = member.largeDonorDonations || 0;
  if (reportedItemized > 0 && options.minConcentrationCoverage > 0) {
    const collected = concentration.totalAmount || 0;
    if (collected < reportedItemized * options.minConcentrationCoverage) {
      return false;
    }
  }

  return true;
}

// Sliding itemization limit based on how easily the donor base could
// coordinate. Returns the default anchor when concentration is unreliable.
export function getTrustAnchor(member, concentration, options = DEFAULT_OPTIONS) {
  const DEFAULT_ANCHOR = 40;

  if (!isConcentrationReliable(member, concentration, options)) {
    return { anchor: DEFAULT_ANCHOR, basis: 'default', nakamotoPercent: null };
  }

  const nakamoto = concentration.nakamotoCoefficient;
  const uniqueDonors = concentration.uniqueDonors;
  const nakamotoPercent = uniqueDonors > 0 ? (nakamoto / uniqueDonors) * 100 : 0;

  if (nakamoto < 50) {
    // Dinner party risk: < 50 people control half the money
    return { anchor: 10, basis: 'dinner-party', nakamotoPercent };
  }
  if (nakamotoPercent < 5) {
    // Elite capture: country club / single gala coordination
    return { anchor: 25, basis: 'elite-capture', nakamotoPercent };
  }
  if (nakamotoPercent < 10) {
    // Standard: factional, requires organization
    return { anchor: 40, basis: 'standard', nakamotoPercent };
  }
  // Movement: high entropy, impossible to coordinate
  return { anchor: 50, basis: 'movement', nakamotoPercent };
}

// Points removed from individualFundingPercent for itemized money beyond
// the trust anchor.
//   capped-quadratic: min(excess^2 / divisor, cap)      (July 2026)
//   excess-money:     the excess itemized money itself, as a share of total
//                     raised - excess% of the individual pool x the pool's
//                     share of total                    (#42)
export function calculateItemizationPenalty(
  itemizedPercent,
  anchor,
  options = DEFAULT_OPTIONS,
  rawIndividualFundingPercent = 0
) {
  const excess = Math.max(0, itemizedPercent - anchor);
  if (excess === 0) {
    return 0;
  }
  if (options.penaltyModel === 'excess-money') {
    return (excess / 100) * rawIndividualFundingPercent;
  }
  return Math.min((excess * excess) / options.quadraticDivisor, options.penaltyCap);
}

// Penalty points from concerning PAC funding; shifts tier thresholds upward
export function calculateTransparencyPenalty(member, options = DEFAULT_OPTIONS) {
  const weights = options.pacWeights || PAC_WEIGHTS;
  if (!member.totalRaised) {
    return 0;
  }

  let totalWeightedConcerningMoney = 0;

  if (member.pacContributions?.length) {
    for (const pac of member.pacContributions) {
      const weight =
        pac.committee_type || pac.designation
          ? getPACTransparencyWeight(pac.committee_type, pac.designation, weights)
          : 1.0;

      if (weight > 1.0) {
        totalWeightedConcerningMoney += pac.amount * weight;
      }
    }
  }

  const concerningPercent = (totalWeightedConcerningMoney / member.totalRaised) * 100;
  return Math.min(Math.floor(concerningPercent), 30);
}

export function getAdjustedThresholds(penaltyPoints) {
  return {
    S: 90 + penaltyPoints,
    A: 75 + penaltyPoints,
    B: 60 + penaltyPoints,
    C: 45 + penaltyPoints,
    D: 30 + penaltyPoints,
    E: 15 + penaltyPoints,
  };
}

// Enhanced tier calculation. Pure: concentration data is passed in, not
// fetched. Returns { tier, individualFundingPercent, detail } where detail
// carries the intermediate values for display/debugging.
export function calculateEnhancedTier(member, concentration = null, options = DEFAULT_OPTIONS) {
  if (!member.totalRaised || member.totalRaised === 0) {
    return { tier: 'N/A', individualFundingPercent: 0, detail: null };
  }

  // Whose money is this? Figures are graded only when they came from one of
  // the member's recorded FEC candidate IDs (issue #41). On 2026-09-26, 35
  // members were showing another person's campaign finances - a sitting
  // senator's, a parent's, a namesake's from the 1980s - and 25 of them
  // carried a letter grade for it. Fails closed: a record that was never
  // stamped is not assumed to be right.
  if (member.fecIdentityVerified !== true) {
    return {
      tier: 'UNVERIFIED',
      disputed: true,
      disputeReason: 'fec-identity-not-verified',
      individualFundingPercent: null,
      detail: { path: 'unverified-identity' },
    };
  }

  // A complete PAC list (every gift, from the FEC's bulk files) is data even
  // when it's empty: no PAC money is a finding, not a gap (#57)
  const hasEnhancedPACData =
    member.pacListComplete === true ||
    (member.pacContributions &&
      member.pacContributions.length > 0 &&
      member.pacContributions.some(pac => pac.committee_type || pac.designation));

  const hasUsableConcentration = isConcentrationReliable(member, concentration, options);

  if (!hasEnhancedPACData && !hasUsableConcentration) {
    // Not enough signal for the enhanced model
    const fallbackTier = calculateTier(member.grassrootsPercent, member.totalRaised);
    return {
      tier: fallbackTier,
      individualFundingPercent: Math.round(member.grassrootsPercent || 0),
      detail: { path: 'fallback' },
    };
  }

  const grassroots = member.grassrootsDonations || 0;
  const itemized = member.largeDonorDonations || 0;
  const individualFundingTotal = grassroots + itemized;

  // Individual donations exceeding total receipts has TWO causes, and they
  // need opposite treatment (2026-07-24):
  //
  //   a) Our own error - figures assembled from different cycles. Cramer hit
  //      170-747% this way and the penalty cap laundered it into S tiers.
  //   b) The FEC's own filing - `receipts` is net of refunds while
  //      `individual_itemized_contributions` is gross, so a committee that
  //      returned money legitimately reports itemized > receipts. Thanedar
  //      files exactly this for cycle 2026 (159%).
  //
  // Ratio cannot separate them (159% vs 170%). What separates them is
  // FIDELITY TO SOURCE: were these numbers written from a single FEC
  // response for the cycle we claim? That is a question about our own
  // accuracy rather than a guess about how campaign finance behaves.
  const exceedsReceipts = individualFundingTotal > member.totalRaised * 1.02;
  const sourceVerified =
    member.financialsVerified === true && member.financialsVerifiedCycle === member.dataCycle;

  if (exceedsReceipts && !sourceVerified) {
    // Ours to explain - do not publish a letter grade for it. Must be a
    // real string, not null: the frontend's tierOrder lookup would return
    // undefined and NaN comparators corrupt the whole sorted list.
    return {
      tier: 'DISPUTED',
      disputed: true,
      disputeReason: 'figures-not-reconciled-to-source',
      individualFundingPercent: null,
      detail: { path: 'disputed' },
    };
  }

  const itemizedPercent =
    individualFundingTotal > 0 ? (itemized / individualFundingTotal) * 100 : 0;

  const rawIndividualFundingPercent = (individualFundingTotal / member.totalRaised) * 100;
  let individualFundingPercent = rawIndividualFundingPercent;

  const { anchor, basis, nakamotoPercent } = getTrustAnchor(member, concentration, options);
  const itemizationPenalty = calculateItemizationPenalty(
    itemizedPercent,
    anchor,
    options,
    rawIndividualFundingPercent
  );
  individualFundingPercent -= itemizationPenalty;

  if (options.floorAtZero) {
    individualFundingPercent = Math.max(0, individualFundingPercent);
  }
  // A verified filing can exceed 100% (refunds - see above). Cap the score so
  // it stays readable, and keep the raw figure for the card's footnote.
  individualFundingPercent = Math.min(100, individualFundingPercent);

  // PAC money traced back to the people who funded each PAC (#57,
  // pacPeopleCredit: dollars, already through each PAC's own concentration
  // test and times PAC_TRACING.credit), so it adds straight to the
  // people-funded share.
  const pacCreditPercent =
    member.pacPeopleCredit > 0 ? (member.pacPeopleCredit / member.totalRaised) * 100 : 0;
  if (pacCreditPercent > 0) {
    individualFundingPercent = Math.min(100, individualFundingPercent + pacCreditPercent);
  }

  const transparencyPenalty = calculateTransparencyPenalty(member, options);
  const thresholds = getAdjustedThresholds(transparencyPenalty);

  let tier;
  if (individualFundingPercent >= thresholds.S) {
    tier = 'S';
  } else if (individualFundingPercent >= thresholds.A) {
    tier = 'A';
  } else if (individualFundingPercent >= thresholds.B) {
    tier = 'B';
  } else if (individualFundingPercent >= thresholds.C) {
    tier = 'C';
  } else if (individualFundingPercent >= thresholds.D) {
    tier = 'D';
  } else if (individualFundingPercent >= thresholds.E) {
    tier = 'E';
  } else {
    tier = 'F';
  }

  return {
    tier,
    individualFundingPercent: Math.round(individualFundingPercent),
    disputed: false,
    // Reconciles to source but reads oddly - shown as a footnote, not a fault
    anomaly: exceedsReceipts ? 'itemized-exceeds-net-receipts' : null,
    detail: {
      path: 'enhanced',
      itemizedPercent: Math.round(itemizedPercent * 10) / 10,
      rawIndividualFundingPercent: Math.round(rawIndividualFundingPercent),
      trustAnchor: anchor,
      trustAnchorBasis: basis,
      nakamotoPercent,
      itemizationPenalty: Math.round(itemizationPenalty * 10) / 10,
      transparencyPenalty,
      // Present whenever PAC money was traced (a complete PAC list), 0 included:
      // the page explains the PAC step only for grades worked out this way
      ...(member.pacListComplete === true || pacCreditPercent > 0
        ? { pacCredit: Math.round(pacCreditPercent * 10) / 10 }
        : {}),
    },
  };
}

/**
 * How much of a PAC's money counts as coming from people, looking through it
 * to its own donors with the same rules as a member's (#57): its share from
 * individuals, less big donations above the allowance its donor
 * concentration earns. Money it got from other committees counts only as far
 * as it can be traced to them (`profile.upstream`: [{ amount, share }], each
 * upstream committee's own share, one level further); the rest is untraced
 * and counts as not-people.
 *   profile: { receipts, individuals, itemized, donors, nakamoto, upstream? }
 * Returns { share, traceable }, both 0-1.
 */
export function pacPeopleShare(profile, options = DEFAULT_OPTIONS) {
  const receipts = profile?.receipts || 0;
  if (receipts <= 0) {
    return { share: 0, traceable: 0 };
  }
  const individuals = Math.max(0, Math.min(profile.individuals || 0, receipts));
  const itemized = Math.max(0, Math.min(profile.itemized || 0, individuals));
  const raw = (individuals / receipts) * 100;
  const itemizedPercent = individuals > 0 ? (itemized / individuals) * 100 : 0;
  const { anchor } = getTrustAnchor(
    { largeDonorDonations: itemized },
    {
      nakamotoCoefficient: profile.nakamoto ?? undefined,
      uniqueDonors: profile.donors ?? undefined,
      totalAmount: itemized,
    },
    options
  );
  const fromPeople = raw - calculateItemizationPenalty(itemizedPercent, anchor, options, raw);
  const upstream = (profile.upstream || []).filter(u => u.amount > 0);
  const traced = Math.min(
    receipts - individuals,
    upstream.reduce((t, u) => t + u.amount, 0)
  );
  const upstreamShare =
    traced > 0
      ? (upstream.reduce((t, u) => t + u.amount * u.share, 0) / receipts) *
        (traced / upstream.reduce((t, u) => t + u.amount, 0))
      : 0;
  return {
    share: Math.max(0, Math.min(1, fromPeople / 100 + upstreamShare)),
    traceable: Math.min(1, (individuals + traced) / receipts),
  };
}

/**
 * Every PAC's people share, traced to `PAC_TRACING.depth` (#57): Map id ->
 * { 1: { share, traceable }, 2: { share, traceable } }. Depth 2 counts money
 * a PAC got from other committees as far as those committees' own donors
 * pass the test. `profiles`: Map id -> pacPeopleShare profile; `upstream`:
 * Map id -> [{ id, amount }], the committees that gave to each PAC.
 */
export function tracePacs(profiles, upstream = new Map(), options = DEFAULT_OPTIONS) {
  const one = new Map([...profiles].map(([id, p]) => [id, pacPeopleShare(p, options)]));
  const out = new Map();
  for (const [id, p] of profiles) {
    const up = (upstream.get(id) || [])
      .filter(u => one.get(u.id)?.traceable > 0)
      .map(u => ({ amount: u.amount, share: one.get(u.id).share }));
    out.set(id, { 1: one.get(id), 2: pacPeopleShare({ ...p, upstream: up }, options) });
  }
  return out;
}

/**
 * The PAC money that counts as people money for a member: each gift times
 * its PAC's traced people share, times the credit. Dollars, for
 * `member.pacPeopleCredit`. `gifts`: [{ id, amount }]; `traced`: tracePacs().
 */
export function pacPeopleCredit(gifts, traced, tracing = PAC_TRACING) {
  if (!tracing.credit || !tracing.depth) {
    return 0;
  }
  const people = gifts.reduce(
    (t, g) => t + g.amount * (traced.get(g.id)?.[tracing.depth]?.share || 0),
    0
  );
  return Math.round(people * tracing.credit * 100) / 100;
}

// ===================================================================
// FINAL-3 (owner's decision, frozen 2026-10-12): the grading that ships.
// A port of the simulation's FINAL-3 settings (scripts/simulate/lib/grid.mjs,
// checked against it member by member: scripts/simulate/check-final3.mjs).
//
//   G = s + b + p + y             money given (own money, loans and transfers out)
//   other politicians' joint funds: their transfers come out of s, b, p, y by
//     the fund's mix and count 0 (still in G)
//   directed gifts e (earmarked through a committee other than ActBlue,
//     WinRed or Democracy Engine) come out of b and the donor test
//   small gifts reported by name: people who gave $200 or less in all are
//     small donors however the campaign reported them; their share of the
//     donor records' money moves from b to s (owner 2026-11-02: "Small
//     gifts are not big donations")
//   the big-donor test, on the donor records of people who gave more than
//     $200, each person's gifts to the member's committees added up:
//     wealthy donors: $7,000 or more in all (two maximum donations, owner
//       2026-11-02) is a wealthy donor, and all their money is Power;
//       w = their share of the big donors' money
//     concentration: n the fewest donors who gave half the big money, of N
//       a = min(0.10 + 0.90·clamp((n/N − 0.025)/0.075, 0, 1), min(1, n/100))
//       (the share of donors, and the ballroom: fewer than 100 people)
//     big money counted = min(b·(1 − w), a·(s + b))
//     no grade when the records cover under half the FEC's itemized total
//   Before 2026-11-02 the test had no wealthy-donor line, and counted small
//   itemized gifts as big donors: the legal limit makes maxed-out donors'
//   gifts equal, so thousands of them read as a movement (a Senate campaign
//   75% big donors graded S), and thousands of $50 online gifts made a
//   campaign's top donors look like a tiny share
//   x = 100·(s + big money counted + PAC money counted + directed counted) / G
//   PAC money counted: gifts × the PAC's traced people share, PEOPLE PACs
//     that pass the people test only, scaled to the PAC money received
//   grade on round(x): S 90, A 75, B 60, C 45, D 30, E 15
// ===================================================================

export const FINAL3 = {
  bands: [
    ['S', 90],
    ['A', 75],
    ['B', 60],
    ['C', 45],
    ['D', 30],
    ['E', 15],
  ],
  // People who gave this much or less in all are small donors, itemized or not
  smallUpTo: 200,
  // Two maximum donations to a candidate (the FEC's limit for 2025-26 is
  // $3,500 for each election): a wealthy donor
  eliteFrom: 7000,
  room: 100, // fewer people than this giving half the big money: a room's worth
  fallback: 0.4, // a PAC's limit when its donor records aren't reliable
  coverage: 0.5, // records must hold half the FEC's big-donor total
  credit: { people: 1 }, // every other group counts 0
  // A campaign that's half or more the candidate's own money can't be graded
  // fairly (owner, 2026-10-23: "I'm not sure they can be graded"): it's
  // shown as Self-funded, never as a letter. Small campaigns keep their
  // grades (owner: "Why change the grade?")
  selfFunded: 0.5,
};
const n0 = x => (Number.isFinite(Number(x)) ? Number(x) : 0);
const clamp01 = (x, lo, hi) => Math.max(lo, Math.min(hi, x));

/**
 * The two concentration limits (0-1) from n, the fewest big donors who gave
 * half the big money, and N, all big donors (over $200): one from how small
 * a share of the big donors that is, one from how few people (under 100:
 * the ballroom). The member page says which one applied.
 */
export function final3Limits(nRaw, NRaw) {
  const n = Math.max(0, Number(nRaw) || 0);
  const N = Math.max(0, Number(NRaw) || 0);
  const share = N > 0 ? n / N : 0;
  return {
    shareLimit: 0.1 + 0.9 * clamp01((share - 0.025) / 0.075, 0, 1),
    roomLimit: Math.min(1, n / FINAL3.room),
  };
}

/**
 * Big-donor money that counts as people's: what the wealthy donors didn't
 * give (`eliteShare`, 0-1, of the big donors' money), within the smaller
 * concentration limit of the money from individuals (`individuals`).
 */
export function final3BigCounted(big, individuals, eliteShare, n, N) {
  const kept = Math.max(0, big) * (1 - clamp01(n0(eliteShare), 0, 1));
  const { shareLimit, roomLimit } = final3Limits(n, N);
  return Math.min(kept, Math.min(shareLimit, roomLimit) * Math.max(0, individuals));
}

/**
 * The group a committee counts as: its sponsor group, with PEOPLE split by
 * the people test (PEOPLE, BOARD, or not tested). `p`: { sponsor: { group },
 * peopleTest: { elected } }.
 */
export function final3Group(p) {
  const g = p?.sponsor?.group || 'nosponsor';
  if (g !== 'people') {
    return g;
  }
  const e = p?.peopleTest?.elected;
  return e === true ? 'people' : e === false ? 'board' : 'untested';
}
const creditOf = p => FINAL3.credit[final3Group(p)] ?? 0;

/**
 * Every committee's share of money traced to people (0-1), two steps back:
 * its own donors (the FEC's small/big split, its own donor test), plus money
 * from committees that fund it, where those count. `pacs`: { [id]: { profile:
 * { receipts, individuals, itemized, smallItemized, donors, nakamoto, elite },
 * fec: { receipts,
 * itemized, unitemized } | null, upstream: [{ id, amount }], sponsor,
 * peopleTest } }. Returns Map id -> share.
 */
export function final3PacShares(pacs) {
  const one = new Map();
  const views = new Map();
  for (const [id, p] of Object.entries(pacs)) {
    const prof = p?.profile;
    if (!prof?.receipts || prof.receipts <= 0) {
      views.set(id, null);
      one.set(id, { share: 0, traceable: 0 });
      continue;
    }
    let R = prof.receipts;
    let ind = Math.max(0, Math.min(prof.individuals || 0, R));
    let it = Math.max(0, Math.min(prof.itemized || 0, ind));
    if (p.fec && n0(p.fec.receipts) > 0) {
      R = n0(p.fec.receipts);
      ind = Math.max(0, Math.min(n0(p.fec.unitemized) + n0(p.fec.itemized), R));
      it = Math.max(0, Math.min(n0(p.fec.itemized), ind));
    }
    const records = prof.itemized || 0;
    // Gifts from people who gave $200 or less in all are small, itemized or not
    const smallRecords = Math.min(records, n0(prof.smallItemized));
    const bigRecords = records - smallRecords;
    const big = records > 0 ? it * (bigRecords / records) : it;
    const N = prof.donors ?? 0;
    const n = prof.nakamoto;
    const hasRecords = n !== null && n !== undefined && N > 0 && Number.isFinite(prof.elite);
    let reliable = hasRecords || big <= 0;
    if (reliable && p.fec && n0(p.fec.itemized) > 0) {
      const fecItemized = n0(p.fec.itemized);
      reliable = fecItemized <= 0 || records >= FINAL3.coverage * fecItemized;
    }
    // The members' big-donor test on the PAC's own donors; a cautious limit
    // where its records are short
    const counted = !reliable
      ? Math.min(big, FINAL3.fallback * ind)
      : final3BigCounted(big, ind, bigRecords > 0 ? n0(prof.elite) / bigRecords : 0, n ?? 0, N);
    const share = (ind - big + counted) / R;
    views.set(id, { R, ind });
    one.set(id, { share: clamp01(share, 0, 1), traceable: Math.min(1, ind / R) });
  }
  const out = new Map();
  for (const [id, p] of Object.entries(pacs)) {
    const v = views.get(id);
    const s1 = one.get(id).share;
    let s2 = s1;
    if (v) {
      const up = (p.upstream || []).filter(u => u.amount > 0 && one.get(u.id)?.traceable > 0);
      const B = up.reduce((t, u) => t + u.amount, 0);
      const traced = Math.min(v.R - v.ind, B);
      const upShare = u => (creditOf(pacs[u.id]) > 0 ? one.get(u.id).share : 0);
      if (traced > 0) {
        s2 = s1 + (up.reduce((t, u) => t + u.amount * upShare(u), 0) / v.R) * (traced / B);
      }
    }
    out.set(id, clamp01(s2, 0, 1));
  }
  return out;
}

/** A grade from a rounded score. */
export function final3Band(score) {
  for (const [letter, from] of FINAL3.bands) {
    if (score >= from) {
      return letter;
    }
  }
  return 'F';
}

/**
 * One member's FINAL-3 grade. `m`: {
 *   pf: the person's figures (combineVehicleTotals), committees with each
 *       joint fund's role, ownFund, toMember (toMemberCounted: what the
 *       member's own reports show arriving, otherFundsCounted) and mix;
 *   whole: the same with the member's own joint funds in full;
 *   reconciliation: { ok, pending }; record: the stored member record;
 *   shapeDirected: { donors, nakamoto, total, elite (over $200), smallItemized,
 *       directedOut: { id: $ } };
 *   gifts, giftsToFunds: [[committee id, $]] }
 * `pacs`, `shares`: final3PacShares' input and output.
 * Returns { tier, score, x, reason?, ...the working }.
 */
export function gradeFinal3(m, pacs, shares) {
  const rec = m.reconciliation || {};
  const invariant = m.whole ? m.whole.invariantsHold : m.pf?.invariantsHold;
  const personLevel = Boolean(
    m.pf && !m.pf.failed && m.pf.totalRaised > 0 && invariant && (rec.ok || rec.pending)
  );
  if (!personLevel) {
    // Figures that mix stored and new sources: no grade
    return { tier: 'N/A', reason: 'mixed-source', withheld: true };
  }
  const pf = m.whole ? { ...m.pf, ...m.whole } : m.pf;
  let s = n0(pf.grassrootsDonations);
  let b = n0(pf.largeDonorDonations);
  let p = n0(pf.pacMoney);
  let y = n0(pf.partyMoney);
  const own = n0(pf.ownMoney?.contributions) + n0(pf.ownMoney?.loans);
  const T = n0(pf.totalRaised);
  const fromOthers = T - own;
  // Other politicians' joint funds: everything they passed on is theirs.
  // The part in the buckets (split there by the fund's mix) comes out of
  // them; the rest isn't in any bucket (a fund that raised nothing this
  // cycle, or money that wasn't donations), so it's added (2026-11-01)
  let j = 0;
  let outside = 0;
  for (const c of (m.pf.committees || []).filter(x => x.role === 'joint' && !x.ownFund)) {
    const t = n0(c.toMemberCounted ?? c.toMember);
    if (t > 0) {
      const mix = c.mix || {};
      s -= t * n0(mix.s);
      b -= t * n0(mix.b);
      p -= t * n0(mix.p);
      y -= t * n0(mix.y);
      const inBuckets = t * (n0(mix.s) + n0(mix.b) + n0(mix.p) + n0(mix.y));
      outside += Math.max(0, t - inBuckets);
      j += Math.max(t, inBuckets);
    }
  }
  const given =
    n0(pf.grassrootsDonations) +
    n0(pf.largeDonorDonations) +
    n0(pf.pacMoney) +
    n0(pf.partyMoney) +
    outside;
  [s, b, p, y] = [s, b, p, y].map(v => Math.max(0, v));
  // Directed gifts: out of big-donor money
  const directed = Object.entries(m.shapeDirected?.directedOut || {}).filter(([, a]) => a > 0);
  const e = Math.min(
    b,
    directed.reduce((t, [, a]) => t + a, 0)
  );
  b -= e;
  // People who gave $200 or less in all are small donors, even when the
  // campaign reported their gifts by name: their share of the donor records'
  // money moves from big to small (2026-11-02)
  const sh = m.shapeDirected;
  const itemized = b;
  const records = n0(sh?.total) + n0(sh?.smallItemized);
  const smallItemized =
    b > 0 && records > 0 ? b * clamp01(n0(sh?.smallItemized) / records, 0, 1) : 0;
  s += smallItemized;
  b -= smallItemized;
  const out = { s, b, p, y, j, e, own, T, smallItemized };
  if (own > 0 && fromOthers < given * 0.98 - 1) {
    return {
      ...out,
      tier: 'DISPUTED',
      reason: 'own-money-exceeds-receipts',
      withheld: true,
    };
  }
  const D = given;
  out.denominator = D;
  if (own > 0 && T > 0 && own / T >= FINAL3.selfFunded) {
    return { ...out, tier: 'SELF-FUNDED', reason: 'self-funded', withheld: true };
  }
  if (!(D > 0)) {
    return { ...out, tier: 'N/A', reason: 'no-money', withheld: true };
  }
  // More money graded than the committees took in (owner, 2026-11-01): the
  // campaign's reports contradict each other, so no grade
  if (given > T * 1.02 + 1000) {
    return { ...out, tier: 'DISPUTED', reason: 'more-than-received', withheld: true };
  }
  const I = s + b;
  const sourceVerified =
    m.record?.financialsVerified === true &&
    m.record?.financialsVerifiedCycle === m.record?.dataCycle;
  if (I > D * 1.02 && !sourceVerified) {
    return {
      ...out,
      tier: 'DISPUTED',
      reason: 'figures-not-reconciled-to-source',
      withheld: true,
    };
  }
  // The big-donor test, on people who gave more than $200 (who net above
  // $0, directed gifts left out): the wealthy donors, and how few gave half
  const N = sh?.donors ?? 0;
  const n = sh?.nakamoto;
  const bigRecords = n0(sh?.total);
  const covered = itemized <= 0 || records >= FINAL3.coverage * itemized;
  const hasRecords = n !== null && n !== undefined && N > 0 && Number.isFinite(sh?.elite);
  const reliable = (hasRecords || b <= 0) && covered;
  if (!reliable) {
    // Not enough of the donor records to tell who gave the big money: no
    // grade until they arrive (in place of a middle allowance, 2026-11-01)
    return { ...out, tier: 'DISPUTED', reason: 'records-incomplete', withheld: true };
  }
  const eliteShare = b > 0 && bigRecords > 0 ? clamp01(n0(sh.elite) / bigRecords, 0, 1) : 0;
  const { shareLimit, roomLimit } = b > 0 ? final3Limits(n, N) : { shareLimit: 1, roomLimit: 1 };
  const counted = b > 0 ? final3BigCounted(b, I, eliteShare, n, N) : 0;
  let x = Math.min(100, ((s + counted) / D) * 100);
  // PAC money counted, never above the PAC money received
  const gifts = [...(m.gifts || []), ...(m.giftsToFunds || [])];
  const giftTotal = gifts.reduce((t, [, amt]) => t + amt, 0);
  const f = giftTotal > 0 ? Math.min(1, p / giftTotal) : 1;
  const C =
    Math.round(
      gifts.reduce((t, [id, amt]) => t + amt * (shares.get(id) || 0) * creditOf(pacs[id]), 0) *
        f *
        100
    ) / 100;
  // Directed gifts at the directing committee's share, never capped
  const scale = e > 0 ? e / directed.reduce((t, [, amt]) => t + amt, 0) : 0;
  const E =
    Math.round(
      directed.reduce(
        (t, [id, amt]) => t + amt * scale * (shares.get(id) || 0) * creditOf(pacs[id]),
        0
      ) * 100
    ) / 100;
  const credit = C + E > 0 ? ((C + E) / D) * 100 : 0;
  x = Math.min(100, x + credit);
  const score = Math.round(x);
  return {
    ...out,
    tier: final3Band(score),
    x,
    score,
    N,
    n: n ?? null,
    shareLimit,
    roomLimit,
    eliteDonors: sh?.eliteDonors ?? null,
    eliteShare,
    elite: b * eliteShare,
    reliable,
    counted,
    fromPeople: s + counted,
    creditDollars: C,
    earmarkDollars: E,
  };
}

/**
 * A FINAL-3 grade's money in three parts, as shares of the money given
 * (owner and Craig, 2026-10-13: how the site shows the grade):
 *   People    = small + big counted + PAC counted + directed counted (the score)
 *   Power     = big donations from wealthy donors ($7,000 or more each),
 *               and the big money the concentration limits leave out
 *   Influence = PAC money not counted + party + other politicians' funds
 *               + directed gifts not counted
 * `detail`: gradeMemberFinal3's detail (dollars). Whole percents that add up
 * to 100, People equal to the score. Null without a FINAL-3 working.
 */
export function peoplePowerInfluence(detail) {
  const d = detail || {};
  if (d.method !== 'final-3' || !(d.given > 0) || !Number.isFinite(d.score)) {
    return null;
  }
  const v = x => (Number.isFinite(Number(x)) ? Math.max(0, Number(x)) : 0);
  const power = Math.max(0, v(d.big) - v(d.bigCounted));
  const influence =
    Math.max(0, v(d.pac) - v(d.pacCounted)) +
    v(d.party) +
    v(d.otherPoliticiansFunds) +
    Math.max(0, v(d.directed) - v(d.directedCounted));
  const people = Math.max(0, Math.min(100, Math.round(d.score)));
  const rest = 100 - people;
  const both = power + influence;
  const powerPct = both > 0 ? Math.round((power / both) * rest) : 0;
  return { people, power: powerPct, influence: rest - powerPct };
}
